import React, { Component, Fragment } from 'react';
import { showPreloader } from 'redux/modules/preloader';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import classNames from 'classnames';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import { hideModal, resetModal } from 'redux/modules/modal';
import { List, ListItem } from '@material-ui/core';

import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import FormField from '../FormField';
import { XIcon, SpinnerIcon, VoucherIconColored } from '../../theme/icons';
import ValidationSchema from './DiscountSchema';
import { checkCrsCredentials } from 'redux/modules/crs';
import { applyMagixDiscount, clearAppliedDiscount } from 'redux/modules/discount';
import _ from 'lodash';
import ERRORS from '../../errors/errorids';
import { DISCOUNTS_CONFIG, MAXIC_DISCOUNT_PROD_TYPE } from '../../config';
import {
	trackDiscountCouponShown,
	trackDiscountCouponError,
	trackInvalidSerial,
	trackEmailSerialMissmatch
} from 'lib/Segment/customTrackers';
import { getTrackingOptions } from 'lib/Segment/helpers';

import i18n from 'i18n/i18n';
const localize = i18n.loc.localize;

const styles = theme => {
	return {
		/* Dialog */
		dialogPaper: {
			minWidth: 444
		},
		dialogWrapper: {
			position: 'relative',
			padding: '15px 10px 15px',
			minHeight: 291
		},
		dialogTitle: {
			'& h6': {
				fontWeight: 600,
				fontSize: '28px',
				lineHeight: 1.4,
				marginBottom: 20,
				textAlign: 'left'
			}
		},
		/* close button */
		closeDialog: {
			position: 'absolute',
			top: 10,
			right: 10
		},
		closeDialogIcon: {
			color: '#6F99BD',
			width: '20px',
			height: '20px'
		},
		/* input field */
		fieldRoot: {
			'&$error': {
				marginTop: 10,
				marginBottom: 15,
				fontSize: '0.8rem'
			}
		},
		field: {
			width: '100%',
			margin: '0 0 24px 0',

			'& input': {
				borderWidth: 1,
				borderStyle: 'solid',
				borderRadius: 5,
				borderColor: theme.palette.secondary.lighter,
				color: theme.palette.text.primary,
				padding: '7px 10px',

				'&:focus': {
					borderColor: theme.palette.primary.main
				},

				'&::placeholder': {
					fontFamily: 'Open Sans, sans-serif',
					fontWeight: 'normal',
					fontStyle: 'normal',
					fontSize: '14px',
					lineHeight: '19px',
					color: theme.palette.text.primary
				}
			},

			'& [id*=helper-text]': {
				paddingLeft: '10px'
			}
		},
		/* buttons */
		button: {
			fontFamily: 'Open Sans, sans-serif',
			borderRadius: 5,
			fontWeight: 600,
			fontSize: 18,
			lineHeight: 1.3,
			opacity: '1 !important',
			color: '#000'
		},
		buttonCancel: {
		},
		buttonPrimary: {
			background: theme.palette.primary.main,
			color: 'white',
			textShadow: 'none',
			'&[class*=contained][class*=disabled]': {
				backgroundColor: '#B3B9C0'
			}
		},
		buttonSubmit: {
			marginLeft: 10
		},
		buttonApply: {

		},
		/* error message */
		errorMessage:  {
			color: theme.palette.error.main,
			fontSize: 14,
			lineHeight: 1.5,
			opacity: 1,
			textAlign: 'left',
			marginTop: -15,
			marginBottom: 15,
			'& a': {
				color: theme.palette.primary.main
			}
		},
		/* loading icon */
		loadingWrapper: {
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)'
		},
		loadingMessage: {
			fontSize: 16,
			lineHeight: 1.5,
			opacity: 1
		},
		loadingIcon: {
			display: 'block',
			height: '24px',
			width: '24px',
			margin: '0 auto 10px',
			fill: '#96DCFD',
			animation: 'spin 2s linear infinite'
		},
		'@keyframes spin': {
			'0': {
				transform: 'rotate(0deg)'
			},
			'100%': {
				transform: 'rotate(360deg)'
			}
		},		
		/* success screen */
		successScreenDescription: {
			fontSize: 16,
			lineHeight: 1.5,
			opacity: 1,
			textAlign: 'left'
		},
		successScreenIcon: {
			margin: '20px 0'
		},
		/* serial number example */
		serialNumberDescription: {
			margin: '-5px 0 0',
			textAlign: 'left',
			color: theme.palette.text.primary,
			opacity: 1
		},
		serialNumberExampleList: {
			margin: '0 0 10px',
			listStyle: 'none'
		},
		serialNumberExampleListItem: {
			textAlign: 'left',
			fontSize: 13,
			lineHeight: 1.5,
			color: theme.palette.text.primary,
			fontFamily: '"Open Sans Xara", Arial, Helvetica, sans-serif',
			padding: 0
		}
	};
};

const errors = {
	[ERRORS.OFFLINE] : localize('discount-modal-error-offline'),
	[ERRORS.CRS_INFO_REQUEST_FAILED] : localize('discount-modal-error-crs-info-request-failed'),
	[ERRORS.CRS_INVALID_CREDENTIALS]: localize('discount-modal-error-crs-invalid-credentials'),
	[ERRORS.CRS_CREDENTIALS_ALREADY_USED]: localize('discount-modal-error-crs-credentials-already-used')
};

const invalidReason = {
	'invalid-serial': localize('discount-modal-invalid-serial'),
	'serial-email-missmatch': localize('discount-modal-serial-email-missmatch')
};
@withStyles(styles)
class DiscountModal extends Component {

	constructor(props) {
		super(props);
		this.state = {
			magixEmail: '',
			productSerial: '',
			xhrInProgress: false,
			crsValidationError: false,
			crsValidationSuccess: false,
			touched: {},
			errors: {}
		};
	}

	render() {
		const { modalShown, resetModal, classes } = this.props;

		return (
			<Dialog
				open={modalShown}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				onExited={resetModal}
				BackdropProps={{ style: { backgroundColor: 'rgba(46, 63, 79, 0.4)' } }}
				classes={{ paper: classes.dialogPaper }}
				PaperProps={{
					style: {
						border: 0,
						width: 309,
						borderRadius: 5,
						boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.08)'
					}
				}}
			>
				<div className={classes.dialogWrapper}>
					<IconButton
						className={classes.closeDialog}
						component='span'
						aria-label='Close'
						onClick={() => this.closeDialog()}
					>
						<XIcon className={classes.closeDialogIcon} />

					</IconButton>
					{(() => {
						if (this.state.xhrInProgress) {
							return this.renderLoading();
						} else if (this.state.crsValidationSuccess) {
							return this.renderSuccessScreen();
						} else {
							return this.renderForm();
						}
					})()}
				</div>
			</Dialog>
		);
	}

	closeDialog() {
		this.props.hideModal();
	}

	applyDiscount() {
		this.props.clearAppliedDiscount();// clears isUpgradePopup and isCouponIdProvided

		this.props.applyMagixDiscount(
			this.state.magixEmail,
			this.state.productSerial,
			this.props.crs.prodCatOfLastValidCombination,
			this.props.crs.tierOfLastValidCombination,
			this.props.crs.prodTypeOfLastValidCombination
		);
		this.closeDialog();
	}

	renderLoading() {
		const { classes } = this.props;
		return (
			<div className={classes.loadingWrapper}>
				<SpinnerIcon className={classes.loadingIcon} />
				<Typography className={classes.loadingMessage} variant="body2">
					{localize('discount-modal-checking')}
				</Typography>
			</div>
		);
	}

	renderErrorMessage() {
		const errorMessage = _.get(this.state, 'errors.globalError');
		const { classes } = this.props;
		trackDiscountCouponError(getTrackingOptions(this.props.company));
		return (
			<Typography className={classes.errorMessage} variant="body2" dangerouslySetInnerHTML={{ __html: errorMessage }} />
		);		
	}

	renderSuccessScreen() {
		const { classes } = this.props;

		let tierKey;

		if (this.props.crs.prodTypeOfLastValidCombination === MAXIC_DISCOUNT_PROD_TYPE.SUBSCRIPTION) {
			tierKey = this.props.crs.tierOfLastValidCombination;
		} else if (this.props.crs.prodTypeOfLastValidCombination === MAXIC_DISCOUNT_PROD_TYPE.PERPETUAL) {
			tierKey = this.props.crs.prodCatOfLastValidCombination;
		}

		const discountPerc = DISCOUNTS_CONFIG[`tier_${tierKey}`].discount * 100;

		trackDiscountCouponShown(getTrackingOptions(this.props.company));
		return (
			<Fragment>
				<DialogTitle
					variant='body1'
					color='default'
					className={classes.dialogTitle}>
					{
						this.props.magixDiscountProdType === MAXIC_DISCOUNT_PROD_TYPE.PERPETUAL &&
						_.get(this.props.crs, 'isFallbackProductReturnedLastValidCombination')
							?
							localize('discount-modal-success-title-generic')
							:
							localize('discount-modal-success-title', discountPerc)
					}
				</DialogTitle>
				<DialogContent>
					<Grid container direction="column">
						<Grid item>
							<Typography className={classes.successScreenDescription} variant="body2">
								{localize(
									this.props.magixDiscountProdType === MAXIC_DISCOUNT_PROD_TYPE.PERPETUAL
										?
										'discount-modal-success-message-perpetual'
										:
										'discount-modal-success-message-subscription'
								)}
							</Typography>
						</Grid>
						<Grid item>
							<VoucherIconColored className={classes.successScreenIcon} />
						</Grid>
						<Grid item container direction="row" justify="flex-end">
							<Grid item>
								<Button
									variant='contained'
									color='primary'
									data-tid="button-discount-modal-apply"
									className={classNames(classes.button, classes.buttonPrimary, classes.buttonApply)}
									onClick={() => this.applyDiscount()}
									disabled={false}>
									{localize('great')}
								</Button>
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>
			</Fragment>
		);		
	}

	renderForm() {
		const { classes } = this.props;
		return (
			<Fragment>
				<DialogTitle
					variant='body1'
					color='default'
					className={classes.dialogTitle}>{localize('discount-modal-title')}
				</DialogTitle>
				<DialogContent>
					<Grid container direction="column">
						{this.state.crsValidationError && this.renderErrorMessage()}
						<Grid item>
							{this.field('magixEmail', localize('discount-modal-email-placeholder'), false, 'email')}
						</Grid>
						<Grid item>
							{this.field('productSerial', localize('discount-modal-serial-placeholder'))}
						</Grid>
						<Grid item>
							<Typography className={classes.serialNumberDescription} variant="body2">
								{localize('discount-modal-serial-number-format-example')}
							</Typography>
							<List className={classes.serialNumberExampleList}>
								<ListItem className={classes.serialNumberExampleListItem}>
									P3-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX
								</ListItem>
								<ListItem className={classes.serialNumberExampleListItem}>
									S2-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX
								</ListItem>
							</List>
						</Grid>				
						<Grid item container direction="row" justify="flex-end">
							<Grid item>
								<Button
									variant='outlined'
									color='primary'
									data-tid="button-discount-modal-cancel"
									className={classNames(classes.button, classes.buttonCancel)}
									onClick={() => this.closeDialog()}
									disabled={false}>
									{localize('cancel')}
								</Button>
							</Grid>
							<Grid item>
								<Button
									variant='contained'
									color='primary'
									data-tid="button-discount-modal-save"
									className={classNames(classes.button, classes.buttonPrimary, classes.buttonSubmit)}
									onClick={() => this.submitForm()}
									disabled={!this.isSubmitEnabled()}>
									{localize('submit')}
								</Button>
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>
			</Fragment>
		);
	}

	isSubmitEnabled() {
		return this.state.magixEmail && this.state.productSerial && 
		(Object.keys(this.state.errors).length === 0 || 
		Object.keys(this.state.errors).length === 1 && 
		_.get(this.state, 'errors.globalError'));
	}

	componentWillReceiveProps(nextProps) {
		// Credentials check
		if (this.props.crs.areCredentialsValid !== nextProps.crs.areCredentialsValid) {
			this.handleCredentialsValidation(nextProps);
		}
	}

	handleCredentialsValidation(nextProps) {
		if (nextProps.crs.areCredentialsValid === true) {
			this.setState({
				xhrInProgress: false,
				crsValidationError: false,
				crsValidationSuccess: true,
				errors: { globalError: null }
			});

		} else if (nextProps.crs.areCredentialsValid === false) {
			// Happens in casecases:
			// - firstMessageId is OFFLINE
			// - General HTTP error when status code != 200 (CRS_INFO_REQUEST_FAILED)
			// - returned json.valid = false, for example when serial is invalid.
			const errorMessage = 
			(nextProps.crs.checkCredentialsInvalidReason && invalidReason[nextProps.crs.checkCredentialsInvalidReason]) ||
			(nextProps.crs.checkCredentialsErrorid && errors[nextProps.crs.checkCredentialsErrorid]) ||
			nextProps.crs.checkCredentialsErrorMessage || 
			localize('discount-modal-error-unknown');

			if (nextProps.crs.checkCredentialsInvalidReason && invalidReason[nextProps.crs.checkCredentialsInvalidReason]) {
				if (nextProps.crs.checkCredentialsInvalidReason === 'invalid-serial') {
					trackInvalidSerial(getTrackingOptions(this.props.company));
				} else if (nextProps.crs.checkCredentialsInvalidReason === 'serial-email-missmatch') {
					trackEmailSerialMissmatch(getTrackingOptions(this.props.company));
				}
			}

			this.setState({
				xhrInProgress: false,
				crsValidationError: true,
				crsValidationSuccess: false,
				errors: { globalError: errorMessage }
			});
		}
	}	

	field(name, placeholder, disabled, type, maxLength) {
		const { classes } = this.props;
		const errorClasses = { root: classes.fieldRoot, error: classes.error };
		return (
			<FormField
				value={this.state[name]}
				autoComplete='off'
				className={classes.field}
				data-tid={`form-crs-validation-${name}`}
				onChange={event => this.onChange(event, name)}
				onBlur={event => this.onBlur(event, name)}
				onKeyPress={event => this.onKeyPress(event, name)}
				id={name}
				placeholder={placeholder}
				{...(type ? { type } : {})}
				margin="normal"
				helperText={this.state.errors[name]}
				error={!!this.state.errors[name]}
				FormHelperTextProps={{ classes: errorClasses }}
				disabled={disabled}
				maxLength={maxLength}
				inputRef={this[name]}
			/>
		);
	}

	onChange(event, name) {
		this.setFieldState(name, event.target.value);
	}

	setFieldState(fieldName, value) {
		this.setState({ [fieldName]: value }, this.validateAll);
	}

	onBlur(event, name) {
		this.setState(prevState => ({
			...prevState,
			touched: { ...prevState.touched, [name]: true }
		}), this.validateAll);
	}

	onKeyPress(event, name) {
		if (!this.isSubmitEnabled()) return;
		if (event.key === 'Enter') {
			this.submitForm();
		}
	}

	submitForm() {
		this.setState({ xhrInProgress: true, crsValidationError: false, crsValidationSuccess: false });
		this.props.checkCrsCredentials(
			this.state.magixEmail,
			this.state.productSerial,
			_.get(this.props.user, 'mainEmail'),
			this.props.magixDiscountProdType
		);
	}

	validateAll() {
		let errors = {};
		for (let field in ValidationSchema) {
			let validate = ValidationSchema[field];
			if (validate) {
				let error = validate(this.state[field], this.state, this.props);
				if (error) {
					errors[field] = error;
				}
			}
		}

		const globalError = _.get(this.state, 'errors.globalError');
		if (globalError) {
			errors['globalError'] = globalError;
		}
		this.setState({ errors });
	}

	updateMagixEmail() {
		this.setState({
			errors: { 'magixEmail': null }
		});
	}

	updateProductSerial() {
		this.setState({
			errors: { 'productSerial': null }
		});
	}
}

DiscountModal.propTypes = {
	companies: PropTypes.object,
	modalShown: PropTypes.bool,
	hideModal: PropTypes.func,
	resetModal: PropTypes.func,
	companyId: PropTypes.string,
	showPreloader: PropTypes.func,
	redirectURL: PropTypes.string,
	classes: PropTypes.object,
	checkCrsCredentials: PropTypes.func.isRequired,
	crs: PropTypes.object.isRequired,
	applyMagixDiscount: PropTypes.func,
	clearAppliedDiscount: PropTypes.func,
	user: PropTypes.object.isRequired,
	magixDiscountProdType: PropTypes.string.isRequired,
	company: PropTypes.object,
};

const mapStateToProps = state => ({
	company: state.companies.rawDataById ? state.companies.rawDataById[state.companies.selectedId] : null,
	user: state.session.user,
	modalShown: state.modal.modalShown,
	companies: state.companies,
	companyId: state.companies.rawDataById[state.companies.selectedId].id,
	crs: state.crs
});

const mapDispatchToProps = dispatch => bindActionCreators({
	hideModal,
	resetModal,
	showPreloader,
	checkCrsCredentials,
	applyMagixDiscount,
	clearAppliedDiscount
}, dispatch);

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(DiscountModal);


// WEBPACK FOOTER //
// ./src/components/Modal/DiscountModal.js